/**
 * BGP AS Path Graph Color Coding
 *
 * This module handles color coding for ASNs based on:
 * - RPKI validation status (for origin ASNs)
 * - Expected vs unexpected upstream ASNs
 * - Validation against test configuration
 */

import { validateOrigin, detectUpstreamLeak } from '@kentik/ui-shared/synthetics/bgp';

/**
 * Apply colors from a palette array to a list of ASNs (round-robin)
 *
 * @param {string[]} asns - Array of ASN IDs
 * @param {object} lookups - Lookup data (ASN names, etc.)
 * @param {string[]} palette - Array of CSS color strings
 * @param {Map|null} asnColorMap - Shared stable color map for ASNs
 * @param {boolean} expected - Whether these ASNs are expected
 * @returns {object} Mapping of ASN ID to color info
 */
const addColorFromPalette = (asns, lookups, palette, asnColorMap = null, expected = true) =>
  asns.reduce((acc, id, i) => {
    if (lookups.asns) {
      const name = lookups.asns[id];
      // Use shared color map if available, otherwise fall back to round-robin
      const color = asnColorMap?.get(id) ?? palette[i % palette.length];

      acc[id] = {
        id,
        name,
        color,
        expected
      };
    }

    return acc;
  }, {});

/**
 * Determine which upstream ASNs are responsible for an unexpected upstream leak.
 *
 * Each route's AS path is evaluated independently against the doc's leak rule
 * (hop-1 not in AllowedUpstream AND the path reaches the transit-free zone).
 * Only the hop-1 neighbours that actually leak are flagged — deeper ASNs in the
 * path are never flagged just for being upstream.
 *
 * @param {string[][]} routePaths - AS paths (origin last) for the visible routes
 * @param {object} config - BGP test configuration
 * @returns {Set<string>} Set of leaking hop-1 ASN IDs
 */
const getLeakingUpstreamASNs = (routePaths, config) => {
  const leaking = new Set();

  routePaths.forEach((asPath) => {
    const { upstreamASNs } = detectUpstreamLeak(config, { asPath });
    upstreamASNs.forEach((asn) => leaking.add(String(asn)));
  });

  return leaking;
};

/**
 * Classify ASNs as expected or unexpected based on validation rules
 *
 * @param {string[]} visibleASNs - Array of visible ASN IDs
 * @param {object} origins - Origin ASN metadata (rpki_status, etc.)
 * @param {Set<string>} leakingUpstreamASNs - Hop-1 ASNs responsible for leaks
 * @param {object} config - BGP test configuration
 * @returns {object} { expected: string[], unexpected: string[] }
 */
const classifyASNs = (visibleASNs, origins, leakingUpstreamASNs, config) => {
  const { origin, check_rpki } = config;

  const [expected, unexpected] = visibleASNs.reduce(
    (acc, curr) => {
      if (origins[curr] && (origin || check_rpki)) {
        // Validate origin ASN
        const { invalid } = validateOrigin(config, {
          origin_asn: parseInt(curr, 10),
          rpki_status: origins[curr].rpki_status
        });
        const i = invalid ? 1 : 0;
        acc[i].push(curr);
      } else if (leakingUpstreamASNs.has(curr)) {
        acc[1].push(curr);
      } else {
        // No validation rules apply - treat as expected
        acc[0].push(curr);
      }

      return acc;
    },
    [[], []]
  );

  return { expected, unexpected };
};

/**
 * Apply color coding to graph data based on validation results
 *
 * @param {object} params - Color coding parameters
 * @param {object} params.config - BGP test configuration
 * @param {object} params.graphData - Graph structure data
 * @param {object} params.lookups - Lookup data (ASN names, IPs, DNS)
 * @returns {object} { data: graphData, lookups: enhancedLookups }
 */
export const applyColorCoding = ({ config, graphData, lookups = {}, chartColors = [], asnColorMap = null }) => {
  const { visibleASNs, origins, routePaths = [], ...rest } = graphData;

  const leakingUpstreamASNs = getLeakingUpstreamASNs(routePaths, config);

  // Classify ASNs as expected or unexpected
  const { expected, unexpected } = classifyASNs(visibleASNs, origins, leakingUpstreamASNs, config);

  // Apply color schemes - both expected and unexpected use palette colors.
  // Unexpected ASNs are visually distinguished via cross-hatch overlays, not color.
  const coloredASNs = {
    ...addColorFromPalette(expected, lookups, chartColors, asnColorMap, true),
    ...addColorFromPalette(unexpected, lookups, chartColors, asnColorMap, false)
  };

  return {
    data: rest,
    lookups: {
      ...lookups,
      asns: coloredASNs,
      // Preserve raw ASN name map so popovers can look up ASNs not in the visible graph
      asnNames: lookups.asns || {}
    }
  };
};
